// helper functions used throughout project
import moment from 'moment-timezone';

import {
  timezones,
  orderings,
  languages,
  attendanceTypes,
  classTypes,
  dayNumbers,
  numberDays,
  daysOfWeek,
} from './Mappings';

import {
  PUBLIC_URL,
} from './Constants';


function prettifyDate(uglyDate) {
  // console.log('uglyDate: ', uglyDate);
  const date = moment(uglyDate);
  return date.format('MMM Do, YYYY ');
}

function prettifyBraintreeDate(uglyDate) {
  const date = new Date(uglyDate * 1000).toDateString().slice(3).split(' ');
  return date[1] + ' ' + date[2] + ', ' + date[3];
}

function prettifyEventbriteDate(uglyDate) {
  const date = new Date(uglyDate).toDateString().slice(3).split(' ');
  return date[1] + ' ' + date[2] + ', ' + date[3];
}

function prettifyOscarDateTimezoneAware(uglyDate, uglyTime, userTimezone, eventTimezone) {
  // console.log('uglyDate: ', uglyDate);
  // console.log('uglyDate: ', uglyTime);
  // console.log('dayChange', dayChange);
  const dateTimeString = uglyDate + ' ' + uglyTime;
  // console.log('userTimezone: ', userTimezone)
  // console.log('eventTimezone: ', eventTimezone)
  // console.log('dateTimeString: ', dateTimeString);
  const rawDate = moment.tz(dateTimeString, eventTimezone);
  // const rawDate = moment.tz(dateTimeString, 'YYYY-MM-DD HH:mm:ss', eventTimezone)//.add(dayChange, 'days');
  // console.log('eventDate: ', rawDate.format('MMMM DD, YYYY'));
  // console.log('eventDate: ', rawDate);
  const userDate = rawDate.tz(userTimezone);
  // console.log('userDate: ', userDate.format('MMMM DD, YYYY'));
  // console.log('userDate: ', userDate);
  const prettyDate = userDate.format('MMMM DD, YYYY');
  return [prettyDate, userDate.format('dddd')];
}

function prettifyOscarDate(uglyDate) {
  // const date = new Date(uglyDate).toDateString().slice(3).split(' ');
  // return date[1] + ' ' + date[2] + ', ' + date[3];
  const date = moment(uglyDate);
  return date.format('MMMM DD, YYYY');
}

function convert24to12hourtime(time) {
  let hours = time.split(':')[0];
  const minutes = time.split(':')[1];
  const suffix = (hours >= 12) ? 'PM' : 'AM';
  // only -12 from hours if it is greater than 12 (if not back at mid night)
  hours = (hours > 12) ? hours - 12 : hours;
  // if 00 then it is 12 am
  hours = (hours === '00') ? 12 : hours;
  // if starts with 0, remove it
  if (hours[0] === '0') {
    hours = hours.substring(1);
  }
  const timeString = hours + ':' + minutes + ' ' + suffix;
  return timeString;
}

function convert12to24hourtime(time) {
  let hour = time.split(':')[0];
  const minuteString = time.split(':')[1];
  if (typeof minuteString === 'undefined') {
    return 'timezone error';
  }
  const minute = minuteString.split(' ')[0];
  hour %= 12;
  if (time.toLowerCase().includes('pm')) {
    hour += 12;
  }
  let str = hour.toString();
  if (str.length === 1) {
    str = '0' + str;
  }
  return str.concat(':' + minute);
}

function convertTimezoneTime(time, userTimezone, classTimezone) {
  const fakeTime = new Date();
  const rawTime = moment.tz(fakeTime, classTimezone).set('hour', convert12to24hourtime(time).substring(0, 2).set('minute', 0));
  // console.log('formatted raw time', rawTime.format('LLLL'));
  const rawDay = rawTime.format('d');
  const userTime = rawTime.tz(userTimezone);
  const formattedTime = userTime.format('h:mma');
  // console.log('formatted user time', userTime.format('LLLL'));
  const daysChanged = userTime.format('d') - rawDay;
  return [formattedTime, daysChanged];
}

function convert24HourTimezoneTime(time, userTimezone, classTimezone) {
  const fakeTime = new Date();
  const rawTime = moment.tz(fakeTime, classTimezone).set('hour', time.substring(0, 2)).set('minute', time.substring(3, 5));
  const rawDay = rawTime.format('d');
  const userTime = rawTime.tz(userTimezone);
  const formattedTime = userTime.format('h:mma');
  const daysChanged = userTime.format('d') - rawDay;
  return [formattedTime, daysChanged];
}

function convert24HourTimezoneTimeNoDayChange(time, userTimezone, classTimezone) {
  const fakeTime = new Date();
  const rawTime = moment.tz(fakeTime, classTimezone).set('hour', time.substring(0, 2)).set('minute', time.substring(3, 5));
  const userTime = rawTime.tz(userTimezone);
  const formattedTime = userTime.format('h:mma');
  // console.log('formattedTime: ', formattedTime)
  return formattedTime;
}

function centsToDollars(cents) {
  const dollars = cents / 100;
  return dollars.toFixed(2);
}

function truncate(string, length) {
  if (string.length > length) {
    // return truncated string
    return string.substring(0, length) + '...';
  }
  // return full string
  return string;
}

// https://stackoverflow.com/questions/175739/built-in-way-in-javascript-to-check-if-a-string-is-a-valid-number
function isNumeric(str) {
  if (typeof str !== 'string') return false; // we only process strings!
  return !isNaN(str) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    && !isNaN(parseFloat(str)); // ...and ensure strings of whitespace fail
}

function createProfileRoute(userName) {
  return `/user/${encodeURIComponent(userName)}`;
}

function createProfileUrl(hostName, userName) {
  return hostName + createProfileRoute(userName);
}

function getTimezoneCodeByName(name) {
  const result = timezones.find(obj => (
    obj.name === name
  ));
  return result ? result.code : undefined;
}

function getTimezoneNameByCode(code) {
  const result = timezones.find(obj => (
    obj.code === code
  ));
  return result ? result.name : code;
}

function getTimezoneObjectByCode(code) {
  if (code === '') {
    return null;
  }
  return timezones.find(obj => (
    obj.code === code
  ));
}

function currentTimeInTimezone(code) {
  const now = new Date();
  return moment.tz(now, code).format('h:mma');
}

function getOrderingNameByCode(code) {
  const result = orderings.find(obj => (
    obj.code === code
  ));
  return result ? result.name : code;
}

function getLanguageNameByCode(code) {
  const result = languages.find(obj => (
    obj.code === code
  ));
  return result ? result.name : code;
}

function getAttendanceNameByCode(code) {
  const result = attendanceTypes.find(obj => (
    obj.code === code
  ));
  return result ? result.name : code;
}

function getReadableAttendanceTypeByCode(code) {
  const readableAttendanceTypes = [
    { code: 'online', name: 'Online' },
    { code: 'inperson', name: 'In-Person' },
    { code: 'hybrid', name: 'Online + In-Person' },
  ];
  const result = readableAttendanceTypes.find(obj => (
    obj.code === code
  ));
  return result ? result.name : code;
}

function validLanguageByCode(code) {
  return languages.some(obj => (
    obj.code === code
  ));
}

function validAttendanceTypeByCode(code) {
  return attendanceTypes.some(obj => (
    obj.code === code
  ));
}

function validOrderingByCode(code) {
  return orderings.some(obj => (
    obj.code === code
  ));
}

function validClassTypeByCode(code) {
  return classTypes.some(obj => (
    obj.code === code
  ));
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function createMarkup(markup) {
  return { __html: markup };
}

function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

function buildYouTubeEmbed(youtubeID) {
  return 'https://www.youtube.com/embed/' + youtubeID;
}

const renderDailySchedule = (classTimesArray, userTimezone, classTimezone, currentDay) => {
  const schedule = [];
  classTimesArray.forEach((classDay) => {
    // console.log(day);
    // console.log(currentDay);
    // console.log('classtimezone: ', classTimezone);
    if (typeof classDay.day === 'undefined') {
      return null;
    }
    if (typeof classDay.start === 'undefined') {
      return null;
    }
    let time = 'timezone error';
    let dayChange = 0;
    try {
      if (classDay.start.toLowerCase().includes('am') || classDay.start.toLowerCase().includes('pm')) {
        [time, dayChange] = convertTimezoneTime(classDay.start, userTimezone, classTimezone);
      } else if (classTimezone === '') {
        // class timezone not present
        time = convert24to12hourtime(classDay.start);
        dayChange = 0;
      } else {
        [time, dayChange] = convert24HourTimezoneTime(classDay.start, userTimezone, classTimezone);
      }
    } catch (error) {
      console.log(error);
      return null;
    }
    // console.log('dayChange', dayChange);
    let theDay = classDay.day;
    if (dayChange === 1) {
      if (classDay.day === 'Sunday') {
        theDay = 'Monday';
      } else {
        let dayNum = numberDays[classDay.day];
        dayNum += 1;
        theDay = dayNumbers[dayNum];
      }
    }
    if (dayChange === -1) {
      if (classDay.day === 'Monday') {
        theDay = 'Sunday';
      } else {
        let dayNum = numberDays[classDay.day];
        dayNum -= 1;
        theDay = dayNumbers[dayNum];
      }
    }
    if (theDay !== currentDay && currentDay !== 'Any Day') return null;
    // if (i === 0) {
    //   return (
    //     <span color="textPrimary">{theDay.substring(0, 3) + ' ' + time}</span>
    //   );
    // }
    // return (
    //   <span color="textPrimary">{', ' + theDay.substring(0, 3) + ' ' + time}</span>
    // );
    schedule.push(theDay.substring(0, 3) + ' ' + time);
  });
  return schedule.join(', ');
};

const renderDailyScheduleNoTimeZone = (classTimesArray) => {
  const schedule = [];
  classTimesArray.forEach((classDay) => {
    const time = convert24to12hourtime(classDay.start);
    schedule.push(classDay.day.substring(0, 3) + ' ' + time);
  });
  return schedule.join(', ');
};

const renderLocationDistance = (niaclass) => {
  if (niaclass.distance === null) {
    return null;
  }
  return ' (' + niaclass.distance.substring(0, 3) + ' mi)';
};

const grabClassTimes = (classObj) => {
  const arrayWithNull = daysOfWeek.map((dayOfWeek) => {
    const output = (classObj[dayOfWeek.toLowerCase()]
      ? {
        day: dayOfWeek,
        start: classObj[`${dayOfWeek.toLowerCase()}_start`],
        end: classObj[`${dayOfWeek.toLowerCase()}_end`],
      }
      : null);
    return output;
  });
  return arrayWithNull.filter(day => !!day);
};

const constructShareLink = (location) => {
  // console.log(location);
  const url = PUBLIC_URL + '/ssr' + location.pathname;
  return url;
};

const shuffleArray = (inArray) => {
  // console.log('shuffling: ', inArray);
  const array = inArray;
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
  return array;
};

const productSortByID = (productList) => {
  // console.log('product list before sort: ', productList);
  function compare(a, b) {
    if (a.id < b.id) {
      return -1;
    }
    if (a.id > b.id) {
      return 1;
    }
    return 0;
  }
  const sortedList = productList.sort(compare);
  // console.log('product list after sort: ', sortedList);
  return sortedList;
};

const lineSortByID = (lineList) => {
  // console.log('line list before sort: ', lineList);
  function compare(a, b) {
    // 2nd to last element (grabbing ID from URL)
    const compA = parseInt(a.product.split('/').reverse()[1], 10);
    const compB = parseInt(b.product.split('/').reverse()[1], 10);
    // console.log('comparing: ', compA, compB);
    if (compA < compB) {
      return -1;
    }
    if (compA > compB) {
      return 1;
    }
    return 0;
  }
  const sortedList = lineList.sort(compare);
  // console.log('line list after sort: ', sortedList);
  return sortedList;
};

export {
  prettifyDate,
  prettifyBraintreeDate,
  prettifyOscarDate,
  prettifyOscarDateTimezoneAware,
  convert24to12hourtime,
  convert12to24hourtime,
  convertTimezoneTime,
  convert24HourTimezoneTime,
  convert24HourTimezoneTimeNoDayChange,
  centsToDollars,
  prettifyEventbriteDate,
  truncate,
  isNumeric,
  createProfileRoute,
  createProfileUrl,
  getTimezoneCodeByName,
  getTimezoneNameByCode,
  getTimezoneObjectByCode,
  currentTimeInTimezone,
  getOrderingNameByCode,
  getLanguageNameByCode,
  getAttendanceNameByCode,
  getReadableAttendanceTypeByCode,
  validLanguageByCode,
  validAttendanceTypeByCode,
  validOrderingByCode,
  validClassTypeByCode,
  capitalizeFirstLetter,
  createMarkup,
  clamp,
  buildYouTubeEmbed,
  renderDailySchedule,
  renderLocationDistance,
  grabClassTimes,
  renderDailyScheduleNoTimeZone,
  constructShareLink,
  shuffleArray,
  productSortByID,
  lineSortByID,
};
